<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import getWootAxios from 'dashboard/helper/getWootAxios';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useRoute } from 'vue-router';

const { accountId } = useAccount();
const showAlert = useAlert;
const route = useRoute();

const isLoading = ref(false);
const activeTab = ref('overview');
const expandedItemId = ref(null);

const pack = ref({
  enabled: false,
  count: 0,
  last_updated_at: null,
  items: [],
  config: {},
  filters: {},
  usage_summary: {
    total_hit_count: 0,
    used_items_count: 0,
    recently_used_items_count: 0,
    top_used_items: [],
  },
  outcome_summary: {
    window_days: 30,
    sampled_response_traces: 0,
    responses_with_brand_memory: 0,
    response_usage_rate: 0,
    handoff_count: 0,
    groundedness_review_count: 0,
    auto_send_allowed_count: 0,
    effective_mode_counts: {},
    surface_counts: {},
  },
  business_outcome_summary: {
    window_days: 30,
    conversations_with_brand_memory: 0,
    resolved_conversations_count: 0,
    resolution_rate: 0,
    bot_handoff_conversations_count: 0,
    bot_handoff_rate: 0,
    csat_response_count: 0,
    csat_avg_rating: null,
    csat_positive_count: 0,
    csat_negative_count: 0,
    csat_positive_rate: 0,
  },
  comparison_summary: {
    window_days: 30,
    cohort_basis: 'latest_response_trace_per_conversation',
    sampled_conversations: 0,
    memory_hit: { conversation_count: 0 },
    no_memory_hit: { conversation_count: 0 },
    deltas: {
      resolution_rate: null,
      bot_handoff_rate: null,
      csat_avg_rating: null,
      csat_positive_rate: null,
    },
  },
  recommendation_summary: {
    promotion_candidates_count: 0,
    stale_cleanup_candidates_count: 0,
    top_promotion_candidates: [],
    top_stale_cleanup_candidates: [],
  },
  recent_sources: [],
  generated_articles: [],
  generation_errors: { dead_count: 0, samples: [] },
});

const dialogRef = ref(null);
const isRebuilding = ref(false);
const isGeneratingArticles = ref(false);
const isRetryingFailed = ref(false);

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'memory', label: 'Memory Items' },
  { key: 'articles', label: 'Generated Articles' },
  { key: 'analytics', label: 'Analytics' },
];

const pillLabel = computed(() => {
  if (!pack.value.enabled) return 'Account Memory: OFF';
  const n = Number(pack.value.count || 0);
  return `Account Memory: ON (${n})`;
});

const pillSub = computed(() => {
  if (!pack.value.enabled) return 'Disabled';
  if (
    currentAssistantId.value &&
    pack.value.config?.assistant_memory_enabled === false
  ) {
    return `Assistant #${currentAssistantId.value}: OFF`;
  }
  return pack.value.last_updated_at
    ? `Updated: ${formatDate(pack.value.last_updated_at)}`
    : 'No items yet';
});

const currentAssistantId = computed(() => {
  const numeric = Number(route?.params?.assistantId);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
});

// KPI computed
const kpiCards = computed(() => {
  const usage = pack.value.usage_summary || {};
  const outcome = pack.value.outcome_summary || {};
  const business = pack.value.business_outcome_summary || {};
  const config = pack.value.config || {};

  return [
    {
      title: 'Total Memory Items',
      value: pack.value.count || 0,
      subtitle: config.max_items ? `of ${config.max_items} max` : '',
      color: 'blue',
    },
    {
      title: 'Total Hits',
      value: usage.total_hit_count || 0,
      subtitle: `${usage.used_items_count || 0} items used`,
      color: 'green',
    },
    {
      title: 'Active Last 7d',
      value: usage.recently_used_items_count || 0,
      subtitle: 'items recently used',
      color: 'purple',
    },
    {
      title: 'Usage Rate',
      value: formatPercent(outcome.response_usage_rate),
      subtitle: `${outcome.responses_with_brand_memory || 0} of ${outcome.sampled_response_traces || 0} responses`,
      color: 'amber',
    },
    {
      title: 'Resolution Rate',
      value: formatPercent(business.resolution_rate),
      subtitle: `${business.resolved_conversations_count || 0} resolved`,
      color: 'emerald',
    },
    {
      title: 'CSAT Average',
      value:
        business.csat_avg_rating != null
          ? business.csat_avg_rating.toFixed(1)
          : 'N/A',
      subtitle: `${business.csat_positive_count || 0} positive / ${business.csat_response_count || 0} total`,
      color: 'rose',
    },
  ];
});

const generatedArticles = computed(() => {
  return Array.isArray(pack.value.generated_articles)
    ? pack.value.generated_articles
    : [];
});

const promotedArticles = computed(() => {
  return (pack.value.items || []).filter(item => item.linked_article_id);
});

const promotionCandidates = computed(() => {
  return pack.value.recommendation_summary?.top_promotion_candidates || [];
});

const staleCandidates = computed(() => {
  return pack.value.recommendation_summary?.top_stale_cleanup_candidates || [];
});

const comparisonData = computed(() => {
  const cmp = pack.value.comparison_summary || {};
  const hit = cmp.memory_hit || {};
  const noHit = cmp.no_memory_hit || {};
  const deltas = cmp.deltas || {};
  return { hit, noHit, deltas, sampled: cmp.sampled_conversations || 0 };
});

const topUsedItems = computed(() => {
  return pack.value.usage_summary?.top_used_items || [];
});

function formatPercent(val) {
  if (val == null || val === 0) return '0%';
  return `${(val * 100).toFixed(1)}%`;
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatDelta(val) {
  if (val == null) return '-';
  const pct = (val * 100).toFixed(1);
  if (val > 0) return `+${pct}%`;
  return `${pct}%`;
}

function deltaClass(val) {
  if (val == null) return '';
  return val > 0 ? 'amp-delta--positive' : val < 0 ? 'amp-delta--negative' : '';
}

function invertedDeltaClass(val) {
  if (val == null) return '';
  return val < 0 ? 'amp-delta--positive' : val > 0 ? 'amp-delta--negative' : '';
}

function statusBadgeClass(status) {
  if (status === 'active') return 'amp-badge--green';
  if (status === 'promoted_to_article') return 'amp-badge--blue';
  if (status === 'superseded_by_article') return 'amp-badge--slate';
  return 'amp-badge--amber';
}

function typeBadgeClass(type) {
  const map = {
    faq: 'amp-type--blue',
    policy: 'amp-type--red',
    do_dont: 'amp-type--orange',
    objection_handling: 'amp-type--purple',
    playbook_snippet: 'amp-type--teal',
    tone_pack_note: 'amp-type--pink',
  };
  return map[type] || 'amp-type--slate';
}

function toggleExpand(id) {
  expandedItemId.value = expandedItemId.value === id ? null : id;
}

const fetchPack = async () => {
  isLoading.value = true;
  try {
    const resp = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack`,
      {
        params: currentAssistantId.value
          ? {
              assistant_id: currentAssistantId.value,
              include_account_shared: true,
            }
          : {},
      }
    );
    pack.value = resp?.data || pack.value;
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

const rebuild = async () => {
  isRebuilding.value = true;
  try {
    await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack/rebuild`,
      { conversations_limit: 50 }
    );
    showAlert('Rebuild started. New memory items will appear shortly.');
  } catch (e) {
    showAlert(
      e?.response?.data?.error || e?.message || 'Failed to start rebuild'
    );
  } finally {
    isRebuilding.value = false;
  }
};

const generateArticles = async () => {
  isGeneratingArticles.value = true;
  try {
    const body = { conversations_limit: 30 };
    if (currentAssistantId.value) body.assistant_id = currentAssistantId.value;
    const resp = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack/generate_articles`,
      body
    );
    const scanned = resp?.data?.conversations_scanned;
    const enqueued = resp?.data?.conversations_enqueued;
    if (scanned != null || enqueued != null) {
      const parts = [];
      if (enqueued != null) parts.push(`enqueued ${enqueued}`);
      if (scanned != null) parts.push(`scanned ${scanned}`);
      showAlert(
        `Article generation started (${parts.join(', ')}). Refresh in a minute.`
      );
    } else {
      showAlert('Article generation started. Refresh in a minute.');
    }
  } catch (e) {
    showAlert(
      e?.response?.data?.error ||
        e?.message ||
        'Failed to start article generation'
    );
  } finally {
    isGeneratingArticles.value = false;
  }
};

const retryFailedJobs = async () => {
  isRetryingFailed.value = true;
  try {
    const resp = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/account_memory_pack/retry_failed_article_jobs`
    );
    const retried = resp?.data?.retried;
    showAlert(`Retried ${retried ?? 0} failed jobs. Refresh in a minute.`);
    await fetchPack();
  } catch (e) {
    showAlert(e?.response?.data?.error || e?.message || 'Failed to retry jobs');
  } finally {
    isRetryingFailed.value = false;
  }
};

const open = async () => {
  await fetchPack();
  dialogRef.value?.open();
};

onMounted(fetchPack);
</script>

<template>
  <div class="amp-pill">
    <button
      type="button"
      class="amp-pill__btn"
      :class="{ 'amp-pill__btn--off': !pack.enabled }"
      @click="open"
    >
      <span
        class="amp-pill__indicator"
        :class="
          pack.enabled ? 'amp-pill__indicator--on' : 'amp-pill__indicator--off'
        "
      />
      <span class="amp-pill__text">
        <span class="amp-pill__title">{{ pillLabel }}</span>
        <span class="amp-pill__sub">{{ pillSub }}</span>
      </span>
    </button>
  </div>

  <Dialog
    ref="dialogRef"
    title=""
    description=""
    width="5xl"
    surface="light"
    :show-confirm-button="false"
    :show-cancel-button="false"
    :overflow-y-auto="true"
  >
    <div class="amp">
      <!-- Header -->
      <div class="amp-header">
        <div class="amp-header__left">
          <h2 class="amp-header__title">Account Memory Pack</h2>
          <span
            class="amp-header__badge"
            :class="
              pack.enabled ? 'amp-header__badge--on' : 'amp-header__badge--off'
            "
          >
            {{ pack.enabled ? 'Active' : 'Inactive' }}
          </span>
          <span
            v-if="pack.config?.live_ingest_enabled"
            class="amp-header__badge amp-header__badge--ingest"
          >
            Live Ingest
          </span>
        </div>
        <div class="amp-header__right">
          <Button
            v-if="pack.enabled"
            color="blue"
            variant="outline"
            size="sm"
            label="Generate Articles"
            :is-loading="isGeneratingArticles"
            :disabled="isGeneratingArticles || isLoading"
            @click="generateArticles"
          />
          <Button
            v-if="pack.enabled"
            color="ruby"
            variant="outline"
            size="sm"
            label="Rebuild Memory"
            :is-loading="isRebuilding"
            :disabled="isRebuilding || isLoading"
            @click="rebuild"
          />
          <Button
            color="slate"
            variant="outline"
            size="sm"
            label="Refresh"
            :is-loading="isLoading"
            :disabled="isLoading"
            @click="fetchPack"
          />
        </div>
      </div>

      <!-- Disabled state -->
      <div v-if="!pack.enabled" class="amp-empty">
        <div class="amp-empty__icon">⚠</div>
        <p class="amp-empty__title">Account Memory Pack is disabled</p>
        <p class="amp-empty__desc">
          Enable it in <strong>Configure Settings → AI</strong> to start
          collecting knowledge from conversations.
        </p>
      </div>

      <!-- Tabs + Content -->
      <template v-else>
        <!-- Tab bar -->
        <div class="amp-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="amp-tabs__btn"
            :class="{ 'amp-tabs__btn--active': activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
            <span v-if="tab.key === 'memory'" class="amp-tabs__count">{{
              pack.count || 0
            }}</span>
            <span v-if="tab.key === 'articles'" class="amp-tabs__count">{{
              generatedArticles.length + promotedArticles.length
            }}</span>
          </button>
        </div>

        <!-- OVERVIEW TAB -->
        <div v-if="activeTab === 'overview'" class="amp-section">
          <!-- KPI Grid -->
          <div class="amp-kpi-grid">
            <div
              v-for="kpi in kpiCards"
              :key="kpi.title"
              class="amp-kpi"
              :class="`amp-kpi--${kpi.color}`"
            >
              <div class="amp-kpi__label">{{ kpi.title }}</div>
              <div class="amp-kpi__value">{{ kpi.value }}</div>
              <div class="amp-kpi__sub">{{ kpi.subtitle }}</div>
            </div>
          </div>

          <!-- Assistant scope notice -->
          <div v-if="currentAssistantId" class="amp-notice">
            <template v-if="pack.config?.assistant_memory_enabled === false">
              Assistant #{{ currentAssistantId }} has account memory
              <strong>disabled</strong> at runtime.
            </template>
            <template v-else>
              Assistant #{{ currentAssistantId }} is
              <strong>using</strong> account memory at runtime.
            </template>
          </div>

          <!-- Config summary -->
          <div class="amp-config-row">
            <div class="amp-config-item">
              <span class="amp-config-label">Max Items</span>
              <span class="amp-config-value">{{
                pack.config?.max_items || '-'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Top-K Retrieval</span>
              <span class="amp-config-value">{{
                pack.config?.top_k || '-'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Char Budget</span>
              <span class="amp-config-value">{{
                pack.config?.char_budget || '-'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Cooldown</span>
              <span class="amp-config-value"
                >{{ pack.config?.live_ingest_cooldown_seconds || '-' }}s</span
              >
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Min Messages</span>
              <span class="amp-config-value">{{
                pack.config?.live_ingest_min_messages || '-'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Auto Articles</span>
              <span class="amp-config-value">{{
                pack.config?.auto_articles_enabled ? 'ON' : 'OFF'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Article Min Msg</span>
              <span class="amp-config-value">{{
                pack.config?.auto_articles_min_messages || '-'
              }}</span>
            </div>
            <div class="amp-config-item">
              <span class="amp-config-label">Publish Articles</span>
              <span class="amp-config-value">{{
                pack.config?.auto_articles_publish_enabled ? 'ON' : 'OFF'
              }}</span>
            </div>
          </div>

          <!-- Top used items -->
          <div v-if="topUsedItems.length" class="amp-subsection">
            <h3 class="amp-subsection__title">Most Used Knowledge</h3>
            <div class="amp-top-list">
              <div
                v-for="item in topUsedItems"
                :key="item.id"
                class="amp-top-item"
              >
                <span class="amp-top-item__rank"
                  >{{ item.hit_count }} hits</span
                >
                <span
                  class="amp-top-item__type"
                  :class="typeBadgeClass(item.item_type)"
                  >{{ item.item_type }}</span
                >
                <span class="amp-top-item__title">{{
                  item.title || '(untitled)'
                }}</span>
                <span v-if="item.last_used_at" class="amp-top-item__date">{{
                  formatDate(item.last_used_at)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Recent conversation sources -->
          <div v-if="(pack.recent_sources || []).length" class="amp-subsection">
            <h3 class="amp-subsection__title">Recent Conversation Sources</h3>
            <div class="amp-sources">
              <div
                v-for="src in pack.recent_sources"
                :key="src.conversation_id"
                class="amp-source-row"
              >
                <div class="amp-source-row__meta">
                  <span class="amp-source-row__id"
                    >Conv #{{ src.conversation_id }}</span
                  >
                  <span v-if="src.source_inbox_id" class="amp-source-row__pill"
                    >Inbox #{{ src.source_inbox_id }}</span
                  >
                  <span v-if="src.portal_slug" class="amp-source-row__pill"
                    >Portal: {{ src.portal_slug }}</span
                  >
                  <span v-if="src.locale" class="amp-source-row__pill">{{
                    src.locale
                  }}</span>
                  <span
                    v-if="src.conversation_status"
                    class="amp-source-row__pill"
                    >{{ src.conversation_status }}</span
                  >
                  <span class="amp-source-row__pill"
                    >{{ src.item_count }} items</span
                  >
                  <span
                    v-if="src.last_extracted_at"
                    class="amp-source-row__date"
                    >{{ formatDate(src.last_extracted_at) }}</span
                  >
                </div>
                <div
                  v-if="(src.titles || []).length"
                  class="amp-source-row__titles"
                >
                  <span
                    v-for="t in src.titles"
                    :key="t"
                    class="amp-source-title-chip"
                    >{{ t }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Recommendations -->
          <div
            v-if="promotionCandidates.length || staleCandidates.length"
            class="amp-subsection"
          >
            <h3 class="amp-subsection__title">Recommendations</h3>
            <div v-if="promotionCandidates.length" class="amp-recommend">
              <div class="amp-recommend__header">
                <span class="amp-recommend__icon amp-recommend__icon--promote"
                  >↑</span
                >
                <span class="amp-recommend__label"
                  >Ready to promote to Article</span
                >
              </div>
              <div class="amp-recommend__chips">
                <span
                  v-for="item in promotionCandidates"
                  :key="item.id"
                  class="amp-chip amp-chip--promote"
                >
                  {{ item.title || item.item_type }}
                  <span class="amp-chip__score">{{
                    item.promotion_candidate_score
                  }}</span>
                </span>
              </div>
            </div>
            <div v-if="staleCandidates.length" class="amp-recommend">
              <div class="amp-recommend__header">
                <span class="amp-recommend__icon amp-recommend__icon--stale"
                  >✕</span
                >
                <span class="amp-recommend__label"
                  >Stale — consider cleanup</span
                >
              </div>
              <div class="amp-recommend__chips">
                <span
                  v-for="item in staleCandidates"
                  :key="item.id"
                  class="amp-chip amp-chip--stale"
                >
                  {{ item.title || item.item_type }}
                  <span class="amp-chip__score">{{ item.freshness_band }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- MEMORY ITEMS TAB -->
        <div v-if="activeTab === 'memory'" class="amp-section">
          <div class="amp-table-wrap">
            <table class="amp-table">
              <thead>
                <tr>
                  <th class="amp-table__th--narrow">ID</th>
                  <th>Type</th>
                  <th>Title / Content</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Linked Article</th>
                  <th class="amp-table__th--right">Hits</th>
                  <th>Freshness</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in pack.items || []"
                  :key="item.id"
                  class="amp-table__row"
                  :class="{
                    'amp-table__row--expanded': expandedItemId === item.id,
                  }"
                  @click="toggleExpand(item.id)"
                >
                  <td class="amp-table__td--mono">{{ item.id }}</td>
                  <td>
                    <span
                      class="amp-type-badge"
                      :class="typeBadgeClass(item.item_type)"
                      >{{ item.item_type }}</span
                    >
                  </td>
                  <td class="amp-table__td--content">
                    <div class="amp-item-title">
                      {{ item.title || '(untitled)' }}
                    </div>
                    <div
                      class="amp-item-content"
                      :class="{
                        'amp-item-content--full': expandedItemId === item.id,
                      }"
                    >
                      {{ item.content }}
                    </div>
                  </td>
                  <td>
                    <span
                      class="amp-badge"
                      :class="statusBadgeClass(item.status)"
                      >{{ item.status }}</span
                    >
                  </td>
                  <td class="amp-table__td--source">
                    <div v-if="item.source" class="amp-source-tag">
                      {{ item.source }}
                    </div>
                    <div v-if="item.locale" class="amp-source-detail">
                      {{ item.locale }}
                    </div>
                    <div
                      v-if="item.source_conversation_id"
                      class="amp-source-detail"
                    >
                      Conv #{{ item.source_conversation_id }}
                    </div>
                    <div v-if="item.source_inbox_id" class="amp-source-detail">
                      Inbox #{{ item.source_inbox_id }}
                    </div>
                  </td>
                  <td>
                    <template v-if="item.linked_article_id">
                      <div class="amp-article-link">
                        <span class="amp-article-link__id"
                          >#{{ item.linked_article_id }}</span
                        >
                        <span
                          v-if="item.linked_article_status"
                          class="amp-badge"
                          :class="
                            item.linked_article_status === 'published'
                              ? 'amp-badge--green'
                              : 'amp-badge--amber'
                          "
                        >
                          {{ item.linked_article_status }}
                        </span>
                      </div>
                      <div
                        v-if="item.linked_article_title"
                        class="amp-article-link__title"
                      >
                        {{ item.linked_article_title }}
                      </div>
                    </template>
                    <span v-else class="amp-muted">—</span>
                  </td>
                  <td class="amp-table__td--right">
                    <div class="amp-hit-count">{{ item.hit_count ?? 0 }}</div>
                    <div v-if="item.last_used_at" class="amp-hit-date">
                      {{ formatDate(item.last_used_at) }}
                    </div>
                  </td>
                  <td>
                    <span
                      class="amp-freshness"
                      :class="`amp-freshness--${item.freshness_band}`"
                    >
                      {{ item.freshness_band || '-' }}
                    </span>
                  </td>
                  <td class="amp-table__td--date">
                    {{ formatDate(item.updated_at) }}
                  </td>
                </tr>
                <tr v-if="!(pack.items || []).length">
                  <td colspan="9" class="amp-table__empty">
                    No memory items yet. Click
                    <strong>Rebuild Memory</strong> to generate from recent
                    conversations.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- GENERATED ARTICLES TAB -->
        <div v-if="activeTab === 'articles'" class="amp-section">
          <div
            v-if="(pack.generation_errors?.dead_count || 0) > 0"
            class="amp-callout amp-callout--error"
          >
            <div class="amp-callout__row">
              <div>
                <div class="amp-callout__title">
                  Article generation failures detected
                </div>
                <div class="amp-callout__meta">
                  {{ pack.generation_errors.dead_count }} failed job(s) are in
                  the dead queue.
                </div>
              </div>
              <Button
                color="ruby"
                variant="outline"
                size="sm"
                label="Retry failed jobs"
                :is-loading="isRetryingFailed"
                :disabled="isRetryingFailed || isLoading"
                @click="retryFailedJobs"
              />
            </div>
            <div
              v-if="(pack.generation_errors.samples || []).length"
              class="amp-callout__body"
            >
              <div
                v-for="s in pack.generation_errors.samples"
                :key="s.jid || `${s.error_class}-${s.conversation_id}`"
                class="amp-callout__sample"
              >
                <span class="amp-callout__pill"
                  >Conv #{{ s.conversation_id }}</span
                >
                <span class="amp-callout__pill amp-callout__pill--muted">{{
                  s.error_class
                }}</span>
                <span class="amp-callout__msg">{{ s.error_message }}</span>
              </div>
            </div>
          </div>

          <div v-if="generatedArticles.length" class="amp-subsection">
            <h3 class="amp-subsection__title">Generated from Conversations</h3>
            <div class="amp-articles-grid">
              <div
                v-for="article in generatedArticles"
                :key="article.id"
                class="amp-article-card"
              >
                <div class="amp-article-card__header">
                  <span
                    class="amp-badge"
                    :class="
                      article.status === 'published'
                        ? 'amp-badge--green'
                        : 'amp-badge--amber'
                    "
                  >
                    {{ article.status || 'draft' }}
                  </span>
                  <span
                    v-if="article.portal_slug"
                    class="amp-type-badge amp-type--slate"
                  >
                    {{ article.portal_slug }}
                  </span>
                </div>
                <h4 class="amp-article-card__title">
                  {{ article.title || '(untitled)' }}
                </h4>
                <p v-if="article.description" class="amp-article-card__content">
                  {{ article.description }}
                </p>
                <div class="amp-article-card__footer">
                  <span class="amp-article-card__meta"
                    >Article #{{ article.id }}</span
                  >
                  <span v-if="article.locale" class="amp-article-card__meta">{{
                    article.locale
                  }}</span>
                  <span
                    v-if="article.source_conversation_id"
                    class="amp-article-card__meta"
                  >
                    From Conv #{{ article.source_conversation_id }}
                  </span>
                  <span
                    v-if="article.updated_at"
                    class="amp-article-card__meta"
                  >
                    Updated {{ formatDate(article.updated_at) }}
                  </span>
                </div>
                <div
                  v-if="(article.tags || []).length"
                  class="amp-article-tags"
                >
                  <span
                    v-for="tag in article.tags"
                    :key="tag"
                    class="amp-tag"
                    >{{ tag }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div v-if="promotedArticles.length" class="amp-subsection">
            <h3 class="amp-subsection__title">Promoted from Memory Items</h3>
            <div class="amp-articles-grid">
              <div
                v-for="item in promotedArticles"
                :key="item.id"
                class="amp-article-card"
              >
                <div class="amp-article-card__header">
                  <span
                    class="amp-badge"
                    :class="
                      item.linked_article_status === 'published'
                        ? 'amp-badge--green'
                        : 'amp-badge--amber'
                    "
                  >
                    {{ item.linked_article_status || 'draft' }}
                  </span>
                  <span
                    class="amp-type-badge"
                    :class="typeBadgeClass(item.item_type)"
                    >{{ item.item_type }}</span
                  >
                </div>
                <h4 class="amp-article-card__title">
                  {{ item.linked_article_title || item.title || '(untitled)' }}
                </h4>
                <p class="amp-article-card__content">{{ item.content }}</p>
                <div class="amp-article-card__footer">
                  <span class="amp-article-card__meta"
                    >Article #{{ item.linked_article_id }}</span
                  >
                  <span
                    v-if="item.linked_article_locale"
                    class="amp-article-card__meta"
                    >{{ item.linked_article_locale }}</span
                  >
                  <span
                    v-if="item.linked_article_portal_slug"
                    class="amp-article-card__meta"
                    >Portal: {{ item.linked_article_portal_slug }}</span
                  >
                  <span class="amp-article-card__meta"
                    >{{ item.hit_count || 0 }} hits</span
                  >
                  <span
                    v-if="item.source_conversation_id"
                    class="amp-article-card__meta"
                    >From Conv #{{ item.source_conversation_id }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!generatedArticles.length && !promotedArticles.length"
            class="amp-empty-tab"
          >
            <p class="amp-empty-tab__title">No knowledge articles yet</p>
            <p class="amp-empty-tab__desc">
              Click <strong>Generate Articles</strong> to automatically create
              internal knowledge base articles from your conversation history.
            </p>
          </div>
        </div>

        <!-- ANALYTICS TAB -->
        <div v-if="activeTab === 'analytics'" class="amp-section">
          <!-- Outcome summary -->
          <div class="amp-subsection">
            <h3 class="amp-subsection__title">Response Outcome (30 days)</h3>
            <div class="amp-stats-grid">
              <div class="amp-stat">
                <div class="amp-stat__label">Sampled Traces</div>
                <div class="amp-stat__value">
                  {{ pack.outcome_summary?.sampled_response_traces || 0 }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Used Brand Memory</div>
                <div class="amp-stat__value">
                  {{ pack.outcome_summary?.responses_with_brand_memory || 0 }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Handoff Triggered</div>
                <div class="amp-stat__value">
                  {{ pack.outcome_summary?.handoff_count || 0 }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Groundedness Review</div>
                <div class="amp-stat__value">
                  {{ pack.outcome_summary?.groundedness_review_count || 0 }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Auto-Send Allowed</div>
                <div class="amp-stat__value">
                  {{ pack.outcome_summary?.auto_send_allowed_count || 0 }}
                </div>
              </div>
            </div>
          </div>

          <!-- Business metrics -->
          <div class="amp-subsection">
            <h3 class="amp-subsection__title">Business Impact (30 days)</h3>
            <div class="amp-stats-grid">
              <div class="amp-stat">
                <div class="amp-stat__label">Conversations w/ Memory</div>
                <div class="amp-stat__value">
                  {{
                    pack.business_outcome_summary
                      ?.conversations_with_brand_memory || 0
                  }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Resolved</div>
                <div class="amp-stat__value">
                  {{
                    pack.business_outcome_summary
                      ?.resolved_conversations_count || 0
                  }}
                </div>
                <div class="amp-stat__sub">
                  {{
                    formatPercent(
                      pack.business_outcome_summary?.resolution_rate
                    )
                  }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">Bot Handoffs</div>
                <div class="amp-stat__value">
                  {{
                    pack.business_outcome_summary
                      ?.bot_handoff_conversations_count || 0
                  }}
                </div>
                <div class="amp-stat__sub">
                  {{
                    formatPercent(
                      pack.business_outcome_summary?.bot_handoff_rate
                    )
                  }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">CSAT Avg</div>
                <div class="amp-stat__value">
                  {{
                    pack.business_outcome_summary?.csat_avg_rating?.toFixed(
                      1
                    ) ?? 'N/A'
                  }}
                </div>
              </div>
              <div class="amp-stat">
                <div class="amp-stat__label">CSAT Positive</div>
                <div class="amp-stat__value">
                  {{ pack.business_outcome_summary?.csat_positive_count || 0 }}
                </div>
                <div class="amp-stat__sub">
                  {{
                    formatPercent(
                      pack.business_outcome_summary?.csat_positive_rate
                    )
                  }}
                </div>
              </div>
            </div>
          </div>

          <!-- Comparison: Hit vs No-Hit -->
          <div class="amp-subsection">
            <h3 class="amp-subsection__title">
              Memory Hit vs No-Hit Comparison
              <span class="amp-subsection__badge"
                >{{ comparisonData.sampled }} conversations sampled</span
              >
            </h3>
            <div class="amp-comparison">
              <div class="amp-comparison__table-wrap">
                <table class="amp-comparison__table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th class="amp-comparison__th--cohort">
                        With Memory
                        <span class="amp-comparison__count"
                          >({{
                            comparisonData.hit.conversation_count || 0
                          }})</span
                        >
                      </th>
                      <th class="amp-comparison__th--cohort">
                        Without Memory
                        <span class="amp-comparison__count"
                          >({{
                            comparisonData.noHit.conversation_count || 0
                          }})</span
                        >
                      </th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Resolution Rate</td>
                      <td>
                        {{ formatPercent(comparisonData.hit.resolution_rate) }}
                      </td>
                      <td>
                        {{
                          formatPercent(comparisonData.noHit.resolution_rate)
                        }}
                      </td>
                      <td
                        :class="
                          deltaClass(comparisonData.deltas.resolution_rate)
                        "
                      >
                        {{ formatDelta(comparisonData.deltas.resolution_rate) }}
                      </td>
                    </tr>
                    <tr>
                      <td>Handoff Rate</td>
                      <td>
                        {{ formatPercent(comparisonData.hit.bot_handoff_rate) }}
                      </td>
                      <td>
                        {{
                          formatPercent(comparisonData.noHit.bot_handoff_rate)
                        }}
                      </td>
                      <td
                        :class="
                          invertedDeltaClass(
                            comparisonData.deltas.bot_handoff_rate
                          )
                        "
                      >
                        {{
                          formatDelta(comparisonData.deltas.bot_handoff_rate)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>CSAT Average</td>
                      <td>
                        {{
                          comparisonData.hit.csat_avg_rating?.toFixed(1) ??
                          'N/A'
                        }}
                      </td>
                      <td>
                        {{
                          comparisonData.noHit.csat_avg_rating?.toFixed(1) ??
                          'N/A'
                        }}
                      </td>
                      <td
                        :class="
                          deltaClass(comparisonData.deltas.csat_avg_rating)
                        "
                      >
                        {{
                          comparisonData.deltas.csat_avg_rating != null
                            ? (comparisonData.deltas.csat_avg_rating > 0
                                ? '+'
                                : '') +
                              comparisonData.deltas.csat_avg_rating.toFixed(2)
                            : '-'
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>CSAT Positive Rate</td>
                      <td>
                        {{
                          formatPercent(comparisonData.hit.csat_positive_rate)
                        }}
                      </td>
                      <td>
                        {{
                          formatPercent(comparisonData.noHit.csat_positive_rate)
                        }}
                      </td>
                      <td
                        :class="
                          deltaClass(comparisonData.deltas.csat_positive_rate)
                        "
                      >
                        {{
                          formatDelta(comparisonData.deltas.csat_positive_rate)
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Effective mode breakdown -->
          <div
            v-if="
              Object.keys(pack.outcome_summary?.effective_mode_counts || {})
                .length
            "
            class="amp-subsection"
          >
            <h3 class="amp-subsection__title">Response Mode Breakdown</h3>
            <div class="amp-mode-bars">
              <div
                v-for="(count, mode) in pack.outcome_summary
                  .effective_mode_counts"
                :key="mode"
                class="amp-mode-bar"
              >
                <span class="amp-mode-bar__label">{{ mode }}</span>
                <div class="amp-mode-bar__track">
                  <div
                    class="amp-mode-bar__fill"
                    :style="{
                      width: `${Math.min((count / (pack.outcome_summary?.sampled_response_traces || 1)) * 100, 100)}%`,
                    }"
                  />
                </div>
                <span class="amp-mode-bar__count">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
</template>

<style scoped>
/* ── Pill ── */
.amp-pill__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid var(--wlab-border-default, #dbe2ea);
  background: var(--wlab-bg-secondary, #f8fafc);
  cursor: pointer;
  transition: all 0.15s ease;
}
.amp-pill__btn:hover {
  border-color: var(--wlab-accent, #3b82f6);
  background: var(--wlab-bg-hover, #f1f5f9);
}
.amp-pill__btn--off {
  opacity: 0.7;
}
.amp-pill__indicator {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.amp-pill__indicator--on {
  background: #22c55e;
  box-shadow: 0 0 4px #22c55e88;
}
.amp-pill__indicator--off {
  background: #94a3b8;
}
.amp-pill__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.amp-pill__title {
  font-size: 10px;
  line-height: 13px;
  font-weight: 700;
  color: var(--wlab-text-primary, #0f172a);
  white-space: nowrap;
}
.amp-pill__sub {
  font-size: 9px;
  line-height: 11px;
  color: var(--wlab-text-muted, #64748b);
  white-space: nowrap;
}

/* ── Modal container ── */
.amp {
  color: #0f172a;
}

/* ── Header ── */
.amp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 16px;
}
.amp-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.amp-header__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.amp-header__badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.amp-header__badge--on {
  background: #dcfce7;
  color: #166534;
}
.amp-header__badge--off {
  background: #fee2e2;
  color: #991b1b;
}
.amp-header__badge--ingest {
  background: #dbeafe;
  color: #1e40af;
}
.amp-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Tabs ── */
.amp-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 16px;
}
.amp-tabs__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.amp-tabs__btn:hover {
  color: #334155;
}
.amp-tabs__btn--active {
  color: #1e40af;
  border-bottom-color: #3b82f6;
}
.amp-tabs__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  font-size: 10px;
  font-weight: 700;
  color: #475569;
}
.amp-tabs__btn--active .amp-tabs__count {
  background: #dbeafe;
  color: #1e40af;
}

/* ── Section ── */
.amp-section {
  min-height: 200px;
}

/* ── KPI Grid ── */
.amp-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}
@media (max-width: 768px) {
  .amp-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
.amp-kpi {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  transition: box-shadow 0.15s ease;
}
.amp-kpi:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.amp-kpi__label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.amp-kpi__value {
  font-size: 22px;
  font-weight: 700;
  margin: 2px 0;
}
.amp-kpi--blue .amp-kpi__value {
  color: #1e40af;
}
.amp-kpi--green .amp-kpi__value {
  color: #166534;
}
.amp-kpi--purple .amp-kpi__value {
  color: #6d28d9;
}
.amp-kpi--amber .amp-kpi__value {
  color: #b45309;
}
.amp-kpi--emerald .amp-kpi__value {
  color: #047857;
}
.amp-kpi--rose .amp-kpi__value {
  color: #be123c;
}
.amp-kpi__sub {
  font-size: 11px;
  color: #94a3b8;
}

/* ── Config row ── */
.amp-config-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}
.amp-config-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f5f9;
  font-size: 11px;
}
.amp-config-label {
  color: #64748b;
  font-weight: 600;
}
.amp-config-value {
  color: #0f172a;
  font-weight: 700;
}

/* ── Notice ── */
.amp-notice {
  padding: 8px 12px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  font-size: 12px;
  color: #1e40af;
  margin-bottom: 16px;
}

/* ── Subsection ── */
.amp-subsection {
  margin-bottom: 20px;
}
.amp-subsection__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.amp-subsection__badge {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  padding: 2px 6px;
  background: #f1f5f9;
  border-radius: 4px;
}

/* ── Top used list ── */
.amp-top-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.amp-top-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #fafbfc;
  border: 1px solid #f1f5f9;
  font-size: 12px;
}
.amp-top-item:hover {
  background: #f1f5f9;
}
.amp-top-item__rank {
  font-weight: 700;
  color: #1e40af;
  min-width: 55px;
}
.amp-top-item__title {
  color: #334155;
  flex: 1;
}
.amp-top-item__date {
  color: #94a3b8;
  font-size: 11px;
}

/* ── Recent sources ── */
.amp-sources {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.amp-source-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 10px 12px;
}
.amp-source-row__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 12px;
}
.amp-source-row__id {
  font-weight: 700;
  color: #0f172a;
}
.amp-source-row__pill {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  padding: 2px 7px;
  border-radius: 999px;
}
.amp-source-row__date {
  color: #94a3b8;
  font-size: 11px;
}
.amp-source-row__titles {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.amp-source-title-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  font-size: 11px;
  font-weight: 500;
}

/* ── Recommendations ── */
.amp-recommend {
  margin-bottom: 10px;
}
.amp-recommend__header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
}
.amp-recommend__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}
.amp-recommend__icon--promote {
  background: #dcfce7;
  color: #166534;
}
.amp-recommend__icon--stale {
  background: #fee2e2;
  color: #991b1b;
}
.amp-recommend__label {
  color: #334155;
}
.amp-recommend__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.amp-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}
.amp-chip--promote {
  background: #eff6ff;
  color: #1e3a8a;
  border: 1px solid #bfdbfe;
}
.amp-chip--stale {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fcd34d;
}
.amp-chip__score {
  font-weight: 700;
  opacity: 0.7;
}

/* ── Table ── */
.amp-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.amp-table {
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.amp-table thead th {
  background: #f8fafc;
  color: #475569;
  text-align: left;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  position: sticky;
  top: 0;
}
.amp-table__th--narrow {
  width: 50px;
}
.amp-table__th--right {
  text-align: right;
}
.amp-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #334155;
}
.amp-table__row {
  cursor: pointer;
  transition: background 0.1s ease;
}
.amp-table__row:hover {
  background: #f8fafc;
}
.amp-table__row--expanded {
  background: #eff6ff;
}
.amp-table tbody tr:last-child td {
  border-bottom: 0;
}
.amp-table__td--mono {
  font-family: monospace;
  color: #94a3b8;
  font-size: 11px;
}
.amp-table__td--right {
  text-align: right;
}
.amp-table__td--date {
  white-space: nowrap;
  font-size: 11px;
  color: #94a3b8;
}
.amp-table__td--content {
  max-width: 320px;
}
.amp-table__td--source {
  font-size: 11px;
}
.amp-table__empty {
  text-align: center;
  padding: 32px 16px;
  color: #94a3b8;
}

/* ── Item content expand ── */
.amp-item-title {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 2px;
}
.amp-item-content {
  color: #64748b;
  font-size: 11px;
  line-height: 1.4;
  max-height: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: max-height 0.2s ease;
}
.amp-item-content--full {
  max-height: 600px;
  overflow: visible;
}

/* ── Badges ── */
.amp-badge {
  display: inline-flex;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}
.amp-badge--green {
  background: #dcfce7;
  color: #166534;
}
.amp-badge--blue {
  background: #dbeafe;
  color: #1e40af;
}
.amp-badge--amber {
  background: #fef3c7;
  color: #92400e;
}
.amp-badge--slate {
  background: #f1f5f9;
  color: #475569;
}

/* ── Type badges ── */
.amp-type-badge {
  display: inline-flex;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}
.amp-type--blue {
  background: #dbeafe;
  color: #1e40af;
}
.amp-type--red {
  background: #fee2e2;
  color: #991b1b;
}
.amp-type--orange {
  background: #ffedd5;
  color: #c2410c;
}
.amp-type--purple {
  background: #ede9fe;
  color: #5b21b6;
}
.amp-type--teal {
  background: #ccfbf1;
  color: #0f766e;
}
.amp-type--pink {
  background: #fce7f3;
  color: #9d174d;
}
.amp-type--slate {
  background: #f1f5f9;
  color: #475569;
}

/* ── Source ── */
.amp-source-tag {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 3px;
  display: inline-block;
  margin-bottom: 2px;
}
.amp-source-detail {
  font-size: 10px;
  color: #94a3b8;
}

/* ── Article link ── */
.amp-article-link {
  display: flex;
  align-items: center;
  gap: 4px;
}
.amp-article-link__id {
  font-family: monospace;
  font-size: 11px;
  color: #1e40af;
}
.amp-article-link__title {
  font-size: 11px;
  color: #475569;
  margin-top: 1px;
}

/* ── Hit count ── */
.amp-hit-count {
  font-weight: 700;
  color: #0f172a;
  text-align: right;
}
.amp-hit-date {
  font-size: 10px;
  color: #94a3b8;
  text-align: right;
}

/* ── Freshness ── */
.amp-freshness {
  display: inline-flex;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
}
.amp-freshness--fresh {
  background: #dcfce7;
  color: #166534;
}
.amp-freshness--warm {
  background: #dbeafe;
  color: #1e40af;
}
.amp-freshness--cooling {
  background: #fef3c7;
  color: #92400e;
}
.amp-freshness--stale {
  background: #fee2e2;
  color: #991b1b;
}

/* ── Muted ── */
.amp-muted {
  color: #cbd5e1;
}

/* ── Articles grid ── */
.amp-articles-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
@media (max-width: 768px) {
  .amp-articles-grid {
    grid-template-columns: 1fr;
  }
}
.amp-article-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  transition: box-shadow 0.15s ease;
}
.amp-article-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.amp-article-card__header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
.amp-article-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
  line-height: 1.3;
}
.amp-article-card__content {
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 0 8px 0;
}
.amp-article-card__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
}
.amp-article-card__meta {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
}

.amp-article-tags {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.amp-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

/* ── Callouts ── */
.amp-callout {
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}
.amp-callout--error {
  border-color: #fecaca;
  background: #fff1f2;
}
.amp-callout__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.amp-callout__title {
  font-size: 13px;
  font-weight: 700;
  color: #7f1d1d;
}
.amp-callout__meta {
  font-size: 12px;
  color: #7f1d1d;
  opacity: 0.85;
  margin-top: 2px;
}
.amp-callout__body {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.amp-callout__sample {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}
.amp-callout__pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #7f1d1d;
  background: #fee2e2;
  border: 1px solid #fecaca;
}
.amp-callout__pill--muted {
  color: #475569;
  background: #f1f5f9;
  border-color: #e2e8f0;
}
.amp-callout__msg {
  color: #7f1d1d;
}

/* ── Empty tab ── */
.amp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}
.amp-empty__icon {
  font-size: 32px;
  margin-bottom: 12px;
}
.amp-empty__title {
  font-size: 16px;
  font-weight: 700;
  color: #334155;
  margin: 0 0 4px 0;
}
.amp-empty__desc {
  font-size: 13px;
  color: #64748b;
  max-width: 360px;
  margin: 0;
}

.amp-empty-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  background: #fafbfc;
}
.amp-empty-tab__icon {
  font-size: 36px;
  margin-bottom: 10px;
}
.amp-empty-tab__title {
  font-size: 15px;
  font-weight: 700;
  color: #334155;
  margin: 0 0 4px 0;
}
.amp-empty-tab__desc {
  font-size: 12px;
  color: #64748b;
  max-width: 340px;
  margin: 0;
}

/* ── Stats grid ── */
.amp-stats-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}
@media (max-width: 768px) {
  .amp-stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.amp-stat {
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fafbfc;
  text-align: center;
}
.amp-stat__label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 2px;
}
.amp-stat__value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}
.amp-stat__sub {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 1px;
}

/* ── Comparison table ── */
.amp-comparison__table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.amp-comparison__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.amp-comparison__table thead th {
  background: #f8fafc;
  padding: 8px 14px;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.amp-comparison__th--cohort {
  text-align: center;
}
.amp-comparison__count {
  font-weight: 500;
  color: #94a3b8;
  font-size: 11px;
}
.amp-comparison__table tbody td {
  padding: 8px 14px;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;
  color: #334155;
}
.amp-comparison__table tbody td:first-child {
  text-align: left;
  font-weight: 600;
}
.amp-comparison__table tbody tr:last-child td {
  border-bottom: 0;
}

/* ── Delta colors ── */
.amp-delta--positive {
  color: #166534;
  font-weight: 700;
}
.amp-delta--negative {
  color: #991b1b;
  font-weight: 700;
}

/* ── Mode bars ── */
.amp-mode-bars {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.amp-mode-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.amp-mode-bar__label {
  width: 100px;
  text-align: right;
  font-weight: 600;
  color: #475569;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.amp-mode-bar__track {
  flex: 1;
  height: 14px;
  border-radius: 7px;
  background: #f1f5f9;
  overflow: hidden;
}
.amp-mode-bar__fill {
  height: 100%;
  border-radius: 7px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  transition: width 0.3s ease;
  min-width: 2px;
}
.amp-mode-bar__count {
  font-weight: 700;
  color: #0f172a;
  min-width: 36px;
}
</style>
